import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { ImagePlus, Pencil, Trash2 } from 'lucide-react'

import {
  isAuthoredNote,
  type CalloutNotesSectionNote,
} from '@/components/CalloutNotesSection'
import {
  PhotoNoteComposer,
  type PhotoNoteComposerHandle,
  type PhotoNoteReplace,
  type PhotoNoteSubmit,
} from '@/components/PhotoNoteComposer'
import { NoteMarkdown } from '@/components/NoteMarkdown'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { RichMarkdownEditor } from '@/components/RichMarkdownEditor'
import { toggleTaskInMarkdown } from '@/lib/noteMarkdown'
import { PhotoGallery } from '@/components/ui/PhotoGallery'
import { ZoomImage } from '@/components/ui/ZoomImage'
import { initials } from '@/lib/initials'
import {
  formatRelativeDateTime,
  formatShortRelativeDateTime,
} from '@/lib/relativeDateTime'
import { cn } from '@/lib/utils'

/**
 * Notes in a side panel.
 *
 * This is the panel counterpart to `CalloutNotesSection` (the full-page notes
 * module): the same note data, rendered compactly for a 24rem sticky panel.
 * `PanelNotes` is the whole thing — feed plus composer; `PanelNoteCard` and
 * `PanelNoteComposer` are its pieces, exported for panels whose feed is not
 * purely notes (the Services activity feed interleaves status events between
 * them) but should still render notes and compose them identically.
 */

/** One note, flattened for the compact card. */
export interface PanelNoteCardNote {
  id: string
  authorName: string
  createdAt: string
  /** Markdown body (text notes) or caption (photo notes). */
  text?: string
  photoUrl?: string
  /** Every photo the note currently shows; single notes may omit this. */
  photoUrls?: string[]
  /** Set when soft-deleted; the card renders as a tombstone. */
  deleted?: { at: string; byName?: string }
}

export interface PanelNoteCardProps {
  note: PanelNoteCardNote
  /** Offered on photo notes only; swaps the image via the shared composer. */
  onReplacePhoto?: () => void
  /**
   * Persists an edited body — the text of a text note, the caption of a photo
   * note. Providing it turns on the pencil and makes markdown checkboxes
   * tappable: a tap opens the inline editor with that one box already
   * flipped, and nothing is written until Save — the same contract as the
   * full-page feeds.
   */
  onSaveEdit?: (next: string) => void
  /** Soft-deletes the note (tombstone), after an in-card confirmation. */
  onDelete?: () => void
}

/** The compact multi-photo strip: small tiles that open the shared gallery. */
function PanelNotePhotoStrip({
  photoUrls,
  caption,
}: {
  photoUrls: string[]
  caption?: string
}) {
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null)
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {photoUrls.map((url, index) => (
        <button
          key={`${url}-${index}`}
          type="button"
          className="h-[52px] w-[52px] cursor-zoom-in overflow-hidden rounded-[7px] border border-[#e7e2d5]"
          aria-label={`Open photo ${index + 1} of ${photoUrls.length}`}
          onClick={() => setGalleryIndex(index)}
        >
          <img
            src={url}
            alt={caption || `Photo ${index + 1}`}
            data-zoom-image="true"
            className="h-full w-full object-cover"
          />
        </button>
      ))}
      {galleryIndex !== null ? (
        <PhotoGallery
          photoUrls={photoUrls}
          caption={caption}
          startIndex={galleryIndex}
          onClose={() => setGalleryIndex(null)}
        />
      ) : null}
    </div>
  )
}

/** One authored note in the compact panel style. */
export function PanelNoteCard({
  note,
  onReplacePhoto,
  onSaveEdit,
  onDelete,
}: PanelNoteCardProps) {
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const savedBody = note.text ?? ''
  // null = read mode; a string is what the inline editor was seeded with —
  // the saved body for a pencil edit, the flipped text for a checkbox tap.
  const [editSeed, setEditSeed] = useState<string | null>(null)
  const [draft, setDraft] = useState(savedBody)

  const startEdit = (seed: string) => {
    setDraft(seed)
    setEditSeed(seed)
  }

  const handleToggleTask = (taskIndex: number) => {
    if (!onSaveEdit) return
    const flipped = toggleTaskInMarkdown(savedBody, taskIndex)
    if (flipped === null) return
    startEdit(flipped)
  }

  if (note.deleted) {
    return (
      <div className="flex gap-2 border-b border-slate-100 bg-slate-50 p-2.5 last:border-b-0">
        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-500">
          {initials(note.authorName)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex items-baseline gap-1.5">
            <span className="text-xs font-semibold text-slate-500">
              {note.deleted.byName ?? note.authorName}
            </span>
            <span className="text-[10.5px] text-slate-400">deleted a note</span>
            <span className="flex-1" />
            <span
              className="font-mono text-[10.5px] text-slate-400"
              title={formatRelativeDateTime(note.deleted.at)}
            >
              {formatShortRelativeDateTime(note.deleted.at)}
            </span>
          </div>
          {note.text ? (
            <div className="text-[12.5px] leading-normal text-slate-400 line-through">
              {note.text}
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div className="flex gap-2 border-b border-slate-100 bg-[#fffdf6] p-2.5 last:border-b-0">
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-200 text-[10px] font-bold text-amber-800">
        {initials(note.authorName)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-baseline gap-1.5">
          <span className="text-xs font-semibold text-slate-900">
            {note.authorName}
          </span>
          <span className="text-[10.5px] text-amber-700">
            {(note.photoUrls?.length ?? 0) > 1
              ? `added ${note.photoUrls?.length} photos`
              : note.photoUrl
                ? 'added a photo'
                : 'wrote a note'}
          </span>
          <span className="flex-1" />
          <span
            className="font-mono text-[10.5px] text-slate-400"
            title={formatRelativeDateTime(note.createdAt)}
          >
            {formatShortRelativeDateTime(note.createdAt)}
          </span>
          {onSaveEdit && editSeed === null ? (
            <button
              type="button"
              className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:bg-amber-100 hover:text-slate-600"
              onClick={() => startEdit(savedBody)}
              aria-label={note.photoUrl ? 'Edit photo caption' : 'Edit note'}
            >
              <Pencil className="h-3 w-3" aria-hidden="true" />
            </button>
          ) : null}
          {onDelete && editSeed === null ? (
            <button
              type="button"
              className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600"
              onClick={() => setConfirmingDelete(true)}
              aria-label="Delete note"
            >
              <Trash2 className="h-3 w-3" aria-hidden="true" />
            </button>
          ) : null}
        </div>
        {(note.photoUrls?.length ?? 0) > 1 ? (
          <PanelNotePhotoStrip
            photoUrls={note.photoUrls ?? []}
            caption={note.text}
          />
        ) : note.photoUrl ? (
          <div className="mt-1.5 flex flex-wrap items-end gap-1.5">
            <div className="h-[52px] w-[52px] overflow-hidden rounded-[7px] border border-[#e7e2d5]">
              <ZoomImage
                src={note.photoUrl}
                alt="Note photo"
                className="h-[52px] w-[52px] object-cover"
              />
            </div>
            {onReplacePhoto ? (
              <button
                type="button"
                onClick={onReplacePhoto}
                className="cursor-pointer text-[10.5px] text-blue-700 hover:underline"
              >
                Replace
              </button>
            ) : null}
          </div>
        ) : null}
        {/* Body sits under the photos, matching the full-page photo note. */}
        {editSeed !== null && onSaveEdit ? (
          <div className="mt-1 space-y-1.5">
            {/* Keyed by seed so a checkbox tap that re-seeds remounts the
                editor with the flipped document. */}
            <RichMarkdownEditor
              key={editSeed}
              initialValue={editSeed}
              onChange={setDraft}
              placeholder={
                note.photoUrl ? 'Describe what these photos show…' : 'Note…'
              }
              ariaLabel={note.photoUrl ? 'Edit photo caption' : 'Edit note'}
              contentClassName="min-h-10 text-[12.5px]"
            />
            <div className="flex justify-end gap-1.5">
              <button
                type="button"
                className="h-7 cursor-pointer rounded-md border border-slate-300 bg-white px-2.5 text-[11.5px] text-slate-600"
                onClick={() => setEditSeed(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={draft === savedBody}
                className={`h-7 rounded-md px-2.5 text-[11.5px] font-medium ${
                  draft !== savedBody
                    ? 'cursor-pointer bg-slate-900 text-white hover:bg-slate-800'
                    : 'cursor-not-allowed bg-slate-200 text-slate-400'
                }`}
                onClick={() => {
                  if (draft === savedBody) return
                  onSaveEdit(draft)
                  setEditSeed(null)
                }}
              >
                Save
              </button>
            </div>
          </div>
        ) : note.text ? (
          <NoteMarkdown
            text={note.text}
            className="mt-1 text-[12.5px] leading-normal text-stone-900"
            onToggleTask={onSaveEdit ? handleToggleTask : undefined}
          />
        ) : null}
      </div>

      {/* Same friction as the full pages: deleting is deliberate, and the
          note survives as a tombstone rather than vanishing. */}
      {onDelete ? (
        <Dialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete note?</DialogTitle>
              <DialogDescription>
                This will remove the note. You won&apos;t see it on future
                screens like invoicing.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <button
                type="button"
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                onClick={() => setConfirmingDelete(false)}
              >
                Keep note
              </button>
              <button
                type="button"
                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
                onClick={() => {
                  setConfirmingDelete(false)
                  onDelete()
                }}
              >
                Delete note
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  )
}

export interface PanelNoteComposerHandle {
  /** Open the file picker to add a new photo note. */
  startAdd: () => void
  /** Open the file picker to replace an existing photo note's image. */
  startReplace: (noteId: string) => void
}

export interface PanelNoteComposerProps {
  /** Add a text note. Return false to keep the draft (e.g. rejected input). */
  onAddNote?: (text: string) => void | boolean | Promise<void | boolean>
  onSubmitPhoto?: (input: PhotoNoteSubmit) => void
  onReplacePhoto?: (noteId: string, input: PhotoNoteReplace) => void
  photoError?: string | null
  onClearPhotoError?: () => void
  /** Placement classes from the host (border, padding). */
  className?: string
}

/**
 * The compact composer row: a one-line note input, the photo entry point, and
 * the shared crop/caption card. While the photo card is open it can outgrow
 * the panel, so the section becomes scrollable to keep Save reachable; when
 * closed it stays fixed (no stray scrollbar from sub-pixel overflow).
 */
export const PanelNoteComposer = forwardRef<
  PanelNoteComposerHandle,
  PanelNoteComposerProps
>(function PanelNoteComposer(
  {
    onAddNote,
    onSubmitPhoto,
    onReplacePhoto,
    photoError,
    onClearPhotoError,
    className,
  },
  ref,
) {
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [photoOpen, setPhotoOpen] = useState(false)
  const photoRef = useRef<PhotoNoteComposerHandle>(null)

  useImperativeHandle(ref, () => ({
    startAdd: () => photoRef.current?.startAdd(),
    startReplace: (noteId) => photoRef.current?.startReplace(noteId),
  }))

  const handleAdd = async () => {
    if (!onAddNote || saving) return
    const text = draft.trim()
    if (!text) return
    setSaving(true)
    try {
      const result = await onAddNote(text)
      if (result !== false) setDraft('')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className={cn(
        'space-y-2',
        photoOpen ? 'min-h-0 overflow-y-auto' : 'shrink-0',
        className,
      )}
    >
      {/* While the photo card is open it takes over: one caption input, one
          Cancel/Save pair — the note row would just be a second, dead input. */}
      {!photoOpen ? (
        <div className="flex gap-2">
          {onAddNote ? (
            <input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void handleAdd()
              }}
              placeholder="Add a note…"
              className="h-8 flex-1 rounded-lg border border-slate-300 bg-white px-2.5 text-[12.5px] text-slate-900"
            />
          ) : (
            <span className="flex-1" />
          )}
          {onSubmitPhoto ? (
            <button
              type="button"
              onClick={() => photoRef.current?.startAdd()}
              title="Add a photo"
              className="inline-flex h-8 shrink-0 cursor-pointer items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 text-[12.5px] font-medium text-slate-700 hover:bg-slate-50"
            >
              <ImagePlus className="h-3.5 w-3.5" aria-hidden />
              Photo
            </button>
          ) : null}
          {onAddNote ? (
            <button
              type="button"
              onClick={() => void handleAdd()}
              disabled={saving || !draft.trim()}
              className="h-8 cursor-pointer rounded-lg bg-slate-900 px-3 text-[12.5px] font-medium text-white disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              Add
            </button>
          ) : null}
        </div>
      ) : null}
      {onSubmitPhoto ? (
        <PhotoNoteComposer
          ref={photoRef}
          onSubmitPhoto={onSubmitPhoto}
          onReplacePhoto={onReplacePhoto}
          error={photoError}
          onClearError={onClearPhotoError}
          onOpenChange={setPhotoOpen}
        />
      ) : null}
    </div>
  )
})

export interface PanelNotesProps {
  /** The thread, oldest first — the same feed shape the full page renders. */
  notes: CalloutNotesSectionNote[]
  /** Heading above the feed, e.g. 'Answers'. */
  label?: string
  emptyLabel?: string
  onAddNote?: (text: string) => void | boolean | Promise<void | boolean>
  onSubmitPhotoNote?: (input: PhotoNoteSubmit) => void
  onReplacePhotoNote?: (noteId: string, input: PhotoNoteReplace) => void
  /**
   * Persists an edited note body — text for text notes, caption for photo
   * notes (the host decides which field from its own note data). Enables the
   * per-card pencil and tappable checkboxes.
   */
  onSaveEditNote?: (noteId: string, next: string) => void
  /** Soft-deletes a note; the thread keeps a tombstone. */
  onDeleteNote?: (noteId: string) => void
  photoError?: string | null
  onClearPhotoError?: () => void
}

function toCardNote(
  note: CalloutNotesSectionNote & { kind?: 'text' | 'photo' },
): PanelNoteCardNote {
  const isPhoto = note.kind === 'photo'
  return {
    id: note.id,
    authorName: note.authorName,
    createdAt: note.createdAt,
    text: isPhoto ? note.caption : note.text,
    photoUrl: isPhoto ? note.photoUrl : undefined,
    photoUrls: isPhoto ? note.photoUrls : undefined,
    deleted: note.deletedAt
      ? { at: note.deletedAt, byName: note.deletedByName }
      : undefined,
  }
}

/**
 * A whole notes thread in a side panel: compact feed plus composer.
 *
 * Status-change and lock entries in the feed are skipped — a panel thread
 * shows the conversation; the record's event history belongs to its page.
 */
export function PanelNotes({
  notes,
  label = 'Notes',
  emptyLabel = 'No notes yet.',
  onAddNote,
  onSubmitPhotoNote,
  onReplacePhotoNote,
  onSaveEditNote,
  onDeleteNote,
  photoError,
  onClearPhotoError,
}: PanelNotesProps) {
  const composerRef = useRef<PanelNoteComposerHandle>(null)
  const authored = notes.filter(isAuthoredNote)

  // The feed reads oldest→newest, so land the scroll on the newest notes —
  // both when the thread swaps out and when a note is added.
  const feedScrollRef = useRef<HTMLDivElement | null>(null)
  const threadKey = authored[0]?.id ?? ''
  useEffect(() => {
    const el = feedScrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [threadKey, authored.length])

  // A flex column, so inside a height-constrained host (the side panel) the
  // feed is what scrolls while the composer stays pinned beneath it; in an
  // unconstrained host everything just takes its natural height.
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex shrink-0 items-center justify-between">
        <h3 className="m-0 text-[11px] font-semibold tracking-wider text-slate-600 uppercase">
          {label}
        </h3>
        <span className="text-[11px] text-slate-400">
          {authored.length} {authored.length === 1 ? 'entry' : 'entries'}
        </span>
      </div>

      <div ref={feedScrollRef} className="min-h-0 flex-1 overflow-y-auto">
        <div className="overflow-hidden rounded-lg border border-slate-200">
          {authored.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-slate-400">
              {emptyLabel}
            </div>
          ) : null}
          {authored.map((note) => (
            <PanelNoteCard
              key={note.id}
              note={toCardNote(note)}
              onReplacePhoto={
                onReplacePhotoNote && note.kind === 'photo' && !note.deletedAt
                  ? () => composerRef.current?.startReplace(note.id)
                  : undefined
              }
              onSaveEdit={
                onSaveEditNote && !note.deletedAt
                  ? (next) => onSaveEditNote(note.id, next)
                  : undefined
              }
              onDelete={
                onDeleteNote && !note.deletedAt
                  ? () => onDeleteNote(note.id)
                  : undefined
              }
            />
          ))}
        </div>
      </div>

      {onAddNote || onSubmitPhotoNote ? (
        <PanelNoteComposer
          ref={composerRef}
          onAddNote={onAddNote}
          onSubmitPhoto={onSubmitPhotoNote}
          onReplacePhoto={onReplacePhotoNote}
          photoError={photoError}
          onClearPhotoError={onClearPhotoError}
        />
      ) : null}
    </div>
  )
}
